import { FirebaseError } from 'firebase/app';

import { isDomainError } from '@/core/errors/DomainError';

export type AppErrorKind =
  | 'permission-denied'
  | 'not-found'
  | 'unavailable'
  | 'sign-in-cancelled'
  | 'sign-in-failed'
  | 'data-integrity'
  | 'domain'
  | 'unknown';

/**
 * The error type the UI works with. Infrastructure translates every foreign
 * error into one of these, so no component ever has to know what a
 * `FirebaseError` code string looks like.
 */
export class AppError extends Error {
  readonly kind: AppErrorKind;
  override readonly cause: unknown;

  constructor(kind: AppErrorKind, message: string, cause?: unknown) {
    super(message);
    this.name = 'AppError';
    this.kind = kind;
    this.cause = cause;
  }
}

const FIREBASE_MESSAGES: Readonly<Record<string, readonly [AppErrorKind, string]>> = {
  'permission-denied': [
    'permission-denied',
    'Dafür fehlt die Berechtigung. Bitte an die Turnierleitung wenden.',
  ],
  'not-found': ['not-found', 'Der gesuchte Eintrag existiert nicht.'],
  unavailable: ['unavailable', 'Keine Verbindung zum Server. Bitte die Internetverbindung prüfen.'],
  'auth/popup-closed-by-user': ['sign-in-cancelled', 'Die Anmeldung wurde abgebrochen.'],
  'auth/cancelled-popup-request': ['sign-in-cancelled', 'Die Anmeldung wurde abgebrochen.'],
  'auth/popup-blocked': [
    'sign-in-failed',
    'Das Anmeldefenster wurde vom Browser blockiert. Bitte Pop-ups für diese Seite erlauben.',
  ],
  'auth/network-request-failed': [
    'unavailable',
    'Keine Verbindung zum Anmeldedienst. Bitte die Internetverbindung prüfen.',
  ],
  'auth/unauthorized-domain': [
    'sign-in-failed',
    'Diese Adresse ist in Firebase nicht für die Anmeldung freigegeben.',
  ],
};

/** Turns anything thrown by Firebase or the domain into an {@link AppError}. */
export function toAppError(error: unknown): AppError {
  if (error instanceof AppError) {
    return error;
  }

  if (isDomainError(error)) {
    return new AppError('domain', error.message, error);
  }

  if (error instanceof FirebaseError) {
    const known = FIREBASE_MESSAGES[error.code];
    return known
      ? new AppError(known[0], known[1], error)
      : new AppError('unknown', `Unerwarteter Fehler (${error.code}).`, error);
  }

  if (error instanceof Error) {
    return new AppError('unknown', error.message, error);
  }

  return new AppError('unknown', 'Unbekannter Fehler.', error);
}
